import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { auth } from '../firebase/config';

const EmailVerificationGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, resendVerificationEmail, refreshUser, logout } = useAuth();
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (!user || user.emailVerified) {
    return <>{children}</>;
  }

  const handleResend = async () => {
    setMessage('');
    setError('');
    setBusy(true);
    try {
      await resendVerificationEmail();
      setMessage('Verification email sent. Check your inbox (and spam folder).');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not send verification email.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleRefresh = async () => {
    setMessage('');
    setError('');
    setBusy(true);
    try {
      await refreshUser();
      if (auth.currentUser?.emailVerified) {
        setMessage('Email verified! You can use the app now.');
      } else {
        setMessage('Still waiting for verification. Click the link in your email, then try again.');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Could not refresh account status.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ backgroundColor: '#000000' }}
    >
      <div
        style={{
          backgroundColor: '#000000',
          border: '2px solid #ffffff',
          maxWidth: '32rem',
          width: '100%',
          padding: '2rem',
        }}
      >
        <h2
          style={{
            color: '#ffffff',
            fontSize: '1.5rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: '1rem',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Verify your email
        </h2>
        <p style={{ color: '#ffffff', fontSize: '0.9rem', marginBottom: '1.5rem', fontFamily: "'Inter', sans-serif" }}>
          We sent a verification link to <strong>{user.email}</strong>. Confirm your email before using upload,
          analysis, and API features.
        </p>

        {message && (
          <p style={{ color: '#2ecc71', fontSize: '0.875rem', marginBottom: '1rem', fontFamily: "'Inter', sans-serif" }}>
            {message}
          </p>
        )}
        {error && (
          <p style={{ color: '#e74c3c', fontSize: '0.875rem', marginBottom: '1rem', fontFamily: "'Inter', sans-serif" }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button
            type="button"
            disabled={busy}
            onClick={handleResend}
            style={{
              background: '#ffffff',
              border: '1px solid #ffffff',
              color: '#000000',
              padding: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              cursor: busy ? 'not-allowed' : 'pointer',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Resend verification email
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={handleRefresh}
            style={{
              background: 'transparent',
              border: '1px solid #ffffff',
              color: '#ffffff',
              padding: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              cursor: busy ? 'not-allowed' : 'pointer',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            I verified — refresh status
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => logout()}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.4)',
              color: '#ffffff',
              padding: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              cursor: busy ? 'not-allowed' : 'pointer',
              fontFamily: "'Inter', sans-serif",
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationGate;
